import type { BuildSalesResolveCatalogRow } from "../builders/buildSalesResolveOrderItems";
import {
  pickSalesResolveBuyerContext,
  salesResolvePartyPayloadFields,
  type SalesResolveBuyerContext,
} from "../builders/pickSalesResolveBuyerContext";
import type { PickSalesResolveBuyerInput } from "../builders/pickSalesResolveBuyerContext";
import type { ResolvedSalesOrder } from "../types/sales";
import { orderLineKey } from "utils/orderResolveLinePricing";
import {
  emptyPartyIdentity,
  type OrderPartyIdentityPayload,
  type PartyIdentity,
} from "../../utils/orderPartyIdentity";

export type CheckoutCartLine = {
  product_id: string;
  variant_id: string;
  quantity?: number;
  unit_retail_price?: number;
  changed_unit_price?: number;
  sku?: string;
  is_taxable?: boolean;
  item_tax_info?: { taxable?: boolean } | Array<{ taxable?: boolean }> | null;
  is_production?: boolean;
  images?: unknown[];
  customizations?: unknown;
  design_id?: string;
  design_name?: string;
  preview_url?: string;
  product_type?: string;
  _id?: unknown;
  id?: unknown;
  line_item_id?: unknown;
};

const EMPTY_MONGO_ID = "000000000000000000000000";

export function normalizeSalesLineItemMongoId(raw: unknown): string {
  if (raw == null) return "";
  if (typeof raw === "object") {
    const oid = (raw as { $oid?: string }).$oid;
    if (oid) return String(oid).trim();
    const str = (raw as { toString?: () => string }).toString?.();
    return str && str !== "[object Object]" ? String(str).trim() : "";
  }
  return String(raw).trim();
}

/**
 * Resolve existing order line Mongo `_id` for PATCH /orders-update.
 * Prefer ids on the cart/UI row; otherwise match saved order.items by product+variant.
 */
export function getCheckoutLineItemId(
  item: {
    _id?: unknown;
    id?: unknown;
    line_item_id?: unknown;
    product_id?: unknown;
    variant_id?: unknown;
  },
  orderItems?: Array<Record<string, unknown>> | null
): string {
  const fromItem =
    normalizeSalesLineItemMongoId(item._id) ||
    normalizeSalesLineItemMongoId(item.id) ||
    normalizeSalesLineItemMongoId(item.line_item_id);
  if (fromItem && fromItem !== EMPTY_MONGO_ID) return fromItem;

  const productId = normalizeSalesLineItemMongoId(item.product_id) || String(item.product_id ?? "").trim();
  const variantId = normalizeSalesLineItemMongoId(item.variant_id) || String(item.variant_id ?? "").trim();
  if (!productId || !Array.isArray(orderItems) || !orderItems.length) return "";

  const match = orderItems.find((it) => {
    const itProduct =
      normalizeSalesLineItemMongoId(it.product_id) || String(it.product_id ?? "").trim();
    const itVariant =
      normalizeSalesLineItemMongoId(it.variant_id) || String(it.variant_id ?? "").trim();
    return (
      itProduct === productId &&
      itVariant === variantId &&
      String(it.inventory_source ?? "") !== "custom"
    );
  });
  if (!match) return "";

  const fromOrder =
    normalizeSalesLineItemMongoId(match._id) ||
    normalizeSalesLineItemMongoId(match.id) ||
    normalizeSalesLineItemMongoId(match.line_item_id);
  return fromOrder && fromOrder !== EMPTY_MONGO_ID ? fromOrder : "";
}

/** Per-line taxable from cart GET (`item_tax_info.taxable`) or top-level `is_taxable`. */
export function readCheckoutCartLineTaxable(item: CheckoutCartLine): boolean {
  const raw = item.item_tax_info;
  const taxInfo = Array.isArray(raw) ? raw[0] : raw;
  if (typeof taxInfo?.taxable === "boolean") return taxInfo.taxable;
  if (typeof item.is_taxable === "boolean") return item.is_taxable;
  return true;
}

export function mapCheckoutCartToResolveCatalogRows(
  cartItems: CheckoutCartLine[]
): BuildSalesResolveCatalogRow[] {
  return (cartItems || []).map((item) => ({
    productId: String(item.product_id),
    variantId: String(item.variant_id),
    quantity: item.quantity,
    unitPrice: Number(item.changed_unit_price ?? item.unit_retail_price ?? 0),
    sku: item.sku,
    is_taxable: readCheckoutCartLineTaxable(item),
    is_production: !!item.is_production,
    images: item.images,
    customizations: item.customizations,
    design_id: item.design_id,
    design_name: item.design_name,
    preview_url: item.preview_url,
    product_type: item.product_type,
  }));
}

export function mergeCheckoutCartFromResolved<T extends CheckoutCartLine>(
  rows: T[],
  resolved: ResolvedSalesOrder | null | undefined
): T[] {
  if (!resolved?.items?.length) return rows;
  const byKey = new Map(
    resolved.items.map((li) => [
      orderLineKey(String(li.product_id ?? ""), String(li.variant_id ?? "")),
      li,
    ])
  );

  return rows.map((row) => {
    const key = orderLineKey(String(row.product_id), String(row.variant_id));
    const li = byKey.get(key);
    if (!li) return row;
    return {
      ...row,
      unit_retail_price: Number(li.unit_retail_price ?? row.unit_retail_price ?? 0),
      changed_unit_price: Number(li.changed_unit_price ?? row.changed_unit_price ?? 0),
      discount_on_unit_quantity: Number(li.discount_on_unit_quantity ?? 0),
      discount_on_total_quantity: Number(li.discount_on_total_quantity ?? 0),
    };
  });
}

export function perItemCouponMapFromResolved(
  resolved: ResolvedSalesOrder | null | undefined
): Record<string, number> {
  const map: Record<string, number> = {};
  for (const li of resolved?.items ?? []) {
    const key = orderLineKey(String(li.product_id ?? ""), String(li.variant_id ?? ""));
    map[key] = Number(li.discount_on_total_quantity ?? 0);
  }
  return map;
}

export function pickAgentCheckoutResolveBuyer(input: {
  userId: string;
  userName: string;
  membershipId?: string | null;
  globalUserId?: string | null;
  organizationId?: string | null;
  userInfo?: { firstName?: string; lastName?: string; email?: string; phone1?: string };
  selectedBillingAddress: unknown;
  selectedShippingAddress: unknown;
  isSameAsBilling: boolean;
}): SalesResolveBuyerContext {
  const membershipId = String(input.membershipId || input.userId || "").trim();
  const organizationId = String(input.organizationId || "").trim();
  const globalUserId = String(input.globalUserId || "").trim();

  return pickSalesResolveBuyerContext({
    values: {
      userType: "agent",
      isSameAsBilling: input.isSameAsBilling,
      userFirstName: String(input.userInfo?.firstName ?? ""),
      userLastName: String(input.userInfo?.lastName ?? ""),
      userEmail: String(input.userInfo?.email ?? ""),
      userPhone: String(input.userInfo?.phone1 ?? ""),
    },
    selectedAgent: {
      value: membershipId,
      membershipId,
      organizationId,
      globalUserId,
      firstName: input.userInfo?.firstName,
      lastName: input.userInfo?.lastName,
      label: input.userName,
    },
    selectedBillingAddress: input.selectedBillingAddress,
    selectedShippingAddress: input.selectedShippingAddress,
  });
}

/** Foundation party fields for POST/PATCH /orders (matches order.go + SignageCreateOrder). */
export function buildCheckoutOrderPartyFields(args: {
  buyerContext: SalesResolveBuyerContext;
  sellerOrgId: string;
  sellerMembershipId?: string | null;
  sellerGlobalUserId?: string | null;
  cartBuyerOrgId?: string | null;
  cartBuyerMembershipId?: string | null;
  cartBuyerGlobalUserId?: string | null;
}): OrderPartyIdentityPayload {
  const seller: PartyIdentity = {
    organizationId: String(args.sellerOrgId || "").trim(),
    membershipId: String(args.sellerMembershipId || "").trim(),
    globalUserId: String(args.sellerGlobalUserId || "").trim(),
  };

  const ctx = args.buyerContext;
  const buyer: PartyIdentity = {
    membershipId: String(
      ctx.buyer.membershipId || ctx.customerId || args.cartBuyerMembershipId || ""
    ).trim(),
    globalUserId: String(
      ctx.buyer.globalUserId || args.cartBuyerGlobalUserId || ""
    ).trim(),
    organizationId: String(
      ctx.buyer.organizationId || args.cartBuyerOrgId || ""
    ).trim(),
  };

  return salesResolvePartyPayloadFields(
    { ...ctx, buyer: buyer.membershipId ? buyer : emptyPartyIdentity() },
    seller
  );
}

export function pickRealtorCheckoutResolveBuyer(
  input: PickSalesResolveBuyerInput & {
    userId: string;
    userName: string;
    isTeammember?: boolean;
    parentBrokerageId?: string | null;
    /** Brokerage org id from auth cookie (`organizationId`). */
    brokerageOrganizationId?: string | null;
    orgname?: string | null;
    userInfo?: { firmDetails?: { companyName?: string }; firstName?: string; lastName?: string };
  }
): SalesResolveBuyerContext {
  const brokerageOrgId = String(
    input.brokerageOrganizationId ??
      (input.isTeammember ? input.parentBrokerageId : null) ??
      ""
  ).trim();
  const brokerageFirmName = input.isTeammember
    ? String(input.orgname ?? input.userName ?? "")
    : String(input.userInfo?.firmDetails?.companyName ?? input.userName ?? "");

  return pickSalesResolveBuyerContext({
    ...input,
    selectedBrokerageFirm: input.selectedBrokerageFirm ?? {
      value: brokerageOrgId,
      organizationId: brokerageOrgId,
      organization_id: brokerageOrgId,
      label: brokerageFirmName,
    },
  });
}
